import PropTypes from 'prop-types';
import React, {useState, useEffect} from 'react';
import {Modal, Form, Input, InputNumber, Table, Space, Button, message, Select, Row, Col, Divider} from 'antd';
import DictSelect from "../../../components/dict/dict-select";
import CoaSelect from "../../manage/cw/coa_select3";
import moment from "moment";
import {PlusOutlined, DeleteOutlined} from '@ant-design/icons';
import {getStockPackageItems, addStockPackageItem, updateStockPackageItem, deleteStockPackageItem} from "../../../lib/api_stock_package";
import {getStocks, getStocksById, addStock, updateStock} from "../../../lib/api_stock";

const {Column} = Table;

const formItemLayout = {
    labelCol: {
        xs: {span: 24},
        sm: {span: 6},
    },
    wrapperCol: {
        xs: {span: 24},
        sm: {span: 18},
    },
};


const Stock_package_modal = ({packageId, onCancel, onOk, year}) => {
    const [form] = Form.useForm();
    const [packageForm] = Form.useForm();
    const [items, setItems] = useState([]);
    const [products, setProducts] = useState([]);
    const [editingItem, setEditingItem] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [loading, setLoading] = useState(false);

    const fetchItems = () => {
        if (packageId > 0) {
            getStocksById(packageId)
                .then(res => {
                    if (res.code === 0) {
                        const packageData = res.data;
                        setItems(packageData.items || []);
                        
                        // 设置套餐基本信息到表单
                        packageForm.setFieldsValue({
                            year: packageData.year,
                            type: packageData.type,
                            coa_id: packageData.coa_id,
                            name: packageData.name,
                            category: packageData.category,
                            specification: packageData.specification,
                            unit: packageData.unit,
                            price: packageData.price,
                            original_price: packageData.original_price,
                            quantity: packageData.quantity,
                            supplier: packageData.supplier,
                            remark: packageData.remark
                        });
                    }
                });
        }
    };

    const fetchProducts = () => {
        getStocks(1, 1000)
            .then(res => {
                if (res.code === 0) {
                    setProducts(res.data.list.filter(p => p.type === 1 && p.id !== packageId));
                }
            });
    };

    useEffect(() => {
        fetchItems();
        fetchProducts();
    }, [packageId]);

    const handleAddItem = () => {
        setEditingItem(null);
        form.resetFields();
        setShowEditModal(true);
    };

    const handleEditItem = (item) => {
        setEditingItem(item);
        form.setFieldsValue(item);
        setShowEditModal(true);
    };

    const handleProductChange = (productId) => {
        const product = products.find(p => p.id === productId);
        if (product) {
            form.setFieldsValue({
                original_price: product.price
            });
        }
    };

    const handleDeleteItem = (itemId) => {
        Modal.confirm({
            title: '确认删除',
            content: '确定要删除这个子商品吗？',
            okText: '确认',
            cancelText: '取消',
            onOk: () => {
                setItems(items.filter(item => item.id !== itemId));
                message.success('删除成功');
            }
        });
    };

    const handleSaveItem = () => {
        form.validateFields()
            .then(values => {
                if (!editingItem) {
                    const existingItem = items.find(item => item.product_id === values.product_id);
                    if (existingItem) {
                        message.error('该商品已在套餐中');
                        return;
                    }
                } else {
                    const existingItem = items.find(item => 
                        item.product_id === values.product_id && item.id !== editingItem.id
                    );
                    if (existingItem) {
                        message.error('该商品已在套餐中');
                        return;
                    }
                }

                if (editingItem) {
                    setItems(items.map(item => 
                        item.id === editingItem.id 
                            ? { ...item, ...values }
                            : item
                    ));
                    message.success('更新成功');
                } else {
                    const newItem = {
                        ...values,
                        id: Date.now()
                    };
                    setItems([...items, newItem]);
                    message.success('添加成功');
                }
                setShowEditModal(false);
            });
    };

    const handleSavePackage = () => {
        packageForm.validateFields()
            .then(values => {
                if (items.length === 0) {
                    message.error('请至少添加一个子商品');
                    return;
                }

                setLoading(true);
                
                // 构建组合套餐数据
                const packageData = {
                    ...values,
                    id: packageId,
                    type: 2, // 组合套餐类型
                    price: values.price || 0,
                    original_price: values.original_price || 0,
                    items: items.map(item => ({
                        product_id: item.product_id,
                        quantity: item.quantity,
                        original_price: item.original_price,
                        product_price: item.product_price,
                        remark: item.remark
                    }))
                };

                if (packageId > 0) {
                    // 更新组合套餐
                    updateStock(packageData)
                        .then(res => {
                            if (res.code === 0) {
                                message.success('更新组合套餐成功');
                                onOk && onOk();
                            } else {
                                message.error(res.message || '更新失败');
                            }
                        })
                        .catch(() => {
                            message.error('更新失败');
                        })
                        .finally(() => {
                            setLoading(false);
                        });
                } else {
                    // 创建组合套餐
                    addStock(packageData)
                        .then(res => {
                            if (res.code === 0) {
                                message.success('创建组合套餐成功');
                                onOk && onOk();
                            } else {
                                message.error(res.message || '创建失败');
                            }
                        })
                        .catch(() => {
                            message.error('创建失败');
                        })
                        .finally(() => {
                            setLoading(false);
                        });
                }
            });
    };

    const getProductName = (productId) => {
        const product = products.find(p => p.id === productId);
        return product ? product.name : '';
    };

    const getProductPrice = (productId) => {
        const product = products.find(p => p.id === productId);
        return product ? product.price : 0;
    };

    // 计算组合套餐的原价（根据子商品自动计算）
    const calculatePackageOriginalPrice = () => {
        if (items.length === 0) return 0;
        
        return items.reduce((total, item) => {
            const product = products.find(p => p.id === item.product_id);
            if (product) {
                return total + (product.price * item.quantity);
            }
            return total;
        }, 0);
    };

    // 计算组合套餐价格（根据子商品套餐内单价自动计算）
    const calculatePackagePrice = () => {
        if (items.length === 0) return 0;
        
        return items.reduce((total, item) => {
            return total + (item.product_price * item.quantity);
        }, 0);
    };

    // 当子商品变化时自动更新套餐价格和原价
    useEffect(() => {
        if (packageForm) {
            const originalPrice = calculatePackageOriginalPrice();
            const packagePrice = calculatePackagePrice();
            packageForm.setFieldsValue({
                original_price: originalPrice,
                price: packagePrice
            });
        }
    }, [items, products]);

    return (
        <>
            <Modal
                title={packageId > 0 ? "编辑组合套餐" : "添加组合套餐"}
                visible={true}
                onOk={handleSavePackage}
                onCancel={onCancel}
                confirmLoading={loading}
                width={1000}
                destroyOnClose={true}
            >
                <Row gutter={16}>
                    {/* 左侧：套餐基本信息 */}
                    <Col span={12}>
                        <Form form={packageForm} {...formItemLayout} initialValues={{year: year, type: 2}}>
                            <Form.Item
                                label="年份"
                                name="year"
                            >
                                <Input disabled />
                            </Form.Item>
                            
                            <Form.Item
                                label="类型"
                                name="type"
                            >
                                <Select 
                                    options={[
                                        {label: '普通商品', value: 1},
                                        {label: '组合套餐', value: 2}
                                    ]} 
                                    disabled
                                />
                            </Form.Item>
                            
                            <Form.Item
                                label="所属科目"
                                name="coa_id"
                            >
                                <CoaSelect year={moment().year()} />
                            </Form.Item>
                            
                            <Form.Item
                                label="套餐名称"
                                name="name"
                                rules={[{required: true, message: '请输入套餐名称'}]}
                            >
                                <Input placeholder="请输入套餐名称" />
                            </Form.Item>
                            
                            <Form.Item
                                label="分类"
                                name="category"
                                rules={[{required: true, message: '请选择分类'}]}
                            >
                                <DictSelect type_value="stocks_category" placeholder="请选择分类" />
                            </Form.Item>
                            
                            <Form.Item
                                label="规格"
                                name="specification"
                            >
                                <Input placeholder="请输入规格" />
                            </Form.Item>
                            
                            <Form.Item
                                label="单位"
                                name="unit"
                                rules={[{required: true, message: '请选择单位'}]}
                            >
                                <DictSelect type_value="stocks_unit" placeholder="请选择单位" />
                            </Form.Item>
                            
                            <Form.Item
                                label="套餐价格"
                                name="price"
                            >
                                <InputNumber 
                                    style={{width: '100%'}} 
                                    min={0} 
                                    precision={2}
                                    placeholder="自动计算"
                                    readOnly
                                />
                            </Form.Item>
                            
                            <Form.Item
                                label="原价"
                                name="original_price"
                            >
                                <InputNumber 
                                    style={{width: '100%'}} 
                                    min={0} 
                                    precision={2}
                                    placeholder="自动计算"
                                    readOnly
                                />
                            </Form.Item>
                            
                            <Form.Item
                                label="库存数量"
                                name="quantity"
                                rules={[{required: true, message: '请输入库存数量'}]}
                            >
                                <InputNumber 
                                    style={{width: '100%'}} 
                                    min={0} 
                                    placeholder="请输入库存数量"
                                />
                            </Form.Item>
                            
                            <Form.Item
                                label="供应商"
                                name="supplier"
                            >
                                <Input placeholder="请输入供应商" />
                            </Form.Item>
                            
                            <Form.Item
                                label="备注"
                                name="remark"
                            >
                                <Input.TextArea rows={3} placeholder="请输入备注信息" />
                            </Form.Item>
                        </Form>
                    </Col>

                    {/* 右侧：子商品管理 */}
                    <Col span={12}>
                        <div style={{marginBottom: 16}}>
                            <Button type="primary" icon={<PlusOutlined/>} onClick={handleAddItem}>
                                添加子商品
                            </Button>
                        </div>
                        
                        <Table
                            dataSource={items}
                            rowKey="id"
                            pagination={false}
                            size="small"
                            scroll={{y: 300}}
                        >
                            <Column
                                title="子商品"
                                dataIndex="product_id"
                                key="product_id"
                                render={(productId) => getProductName(productId)}
                            />
                            <Column
                                title="数量"
                                dataIndex="quantity"
                                key="quantity"
                                width={80}
                            />
                            <Column
                                title="原始单价"
                                dataIndex="original_price"
                                key="original_price"
                                width={100}
                                render={(price) => price ? `¥${price}` : ''}
                            />
                            <Column
                                title="套餐内单价"
                                dataIndex="product_price"
                                key="product_price"
                                width={100}
                                render={(price) => price ? `¥${price}` : ''}
                            />
                            <Column
                                title="操作"
                                key="action"
                                width={120}
                                render={(text, record) => (
                                    <Space>
                                        <Button type="link" size="small" onClick={() => handleEditItem(record)}>
                                            编辑
                                        </Button>
                                        <Button type="link" danger size="small" onClick={() => handleDeleteItem(record.id)}>
                                            删除
                                        </Button>
                                    </Space>
                                )}
                            />
                        </Table>
                    </Col>
                </Row>
            </Modal>

            {/* 子商品编辑弹窗 */}
            <Modal
                title={editingItem ? "编辑子商品" : "添加子商品"}
                visible={showEditModal}
                onOk={handleSaveItem}
                onCancel={() => setShowEditModal(false)}
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        label="子商品"
                        name="product_id"
                        rules={[{required: true, message: '请选择子商品'}]}
                    >
                        <Select 
                            placeholder="请选择子商品" 
                            onChange={handleProductChange}
                            disabled={!!editingItem}
                        >
                            {products.map(product => {
                                const isDisabled = items.some(item => 
                                    item.product_id === product.id && 
                                    (!editingItem || editingItem.id !== item.id)
                                );
                                return (
                                    <Select.Option key={product.id} value={product.id} disabled={isDisabled}>
                                        {product.name}
                                    </Select.Option>
                                );
                            })}
                        </Select>
                    </Form.Item>
                    <Form.Item
                        label="数量"
                        name="quantity"
                        rules={[{required: true, message: '请输入数量'}]}
                    >
                        <InputNumber style={{width: '100%'}} min={1}/>
                    </Form.Item>
                    <Form.Item
                        label="原始单价"
                        name="original_price"
                        rules={[{required: true, message: '请输入原始单价'}]}
                    >
                        <InputNumber style={{width: '100%'}} min={0} precision={2}/>
                    </Form.Item>
                    <Form.Item
                        label="套餐内单价"
                        name="product_price"
                        rules={[{required: true, message: '请输入单价'}]}
                    >
                        <InputNumber style={{width: '100%'}} min={0} precision={2}/>
                    </Form.Item>
                    <Form.Item
                        label="备注"
                        name="remark"
                    >
                        <Input.TextArea rows={3}/>
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};

Stock_package_modal.propTypes = {
    packageId: PropTypes.number.isRequired,
    onCancel: PropTypes.func,
    onOk: PropTypes.func,
};

export default Stock_package_modal;